import React, { Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { QuoteProvider } from "./contexts/QuoteContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { PageTracker } from "./components/PageTracker";
import { Loader2 } from "lucide-react";

// Critical path — loaded eagerly (homepage + product pages)
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";

// ===== CODE-SPLIT: lazy-loaded pages =====
const QuotePage = React.lazy(() => import("./pages/QuotePage"));
const CartCheckoutPage = React.lazy(() => import("./pages/CartCheckoutPage"));
const ContactPage = React.lazy(() => import("./pages/ContactPage"));
const ForBuyersPage = React.lazy(() => import("./pages/ForBuyersPage"));
const ForSuppliersPage = React.lazy(() => import("./pages/ForSuppliersPage"));
const ServicesPage = React.lazy(() => import("./pages/ServicesPage"));
const PersonalShopperPage = React.lazy(() => import("./pages/PersonalShopperPage"));
const PickupDeliveryPage = React.lazy(() => import("./pages/PickupDeliveryPage"));
const CompanyRegistrationPage = React.lazy(() => import("./pages/CompanyRegistrationWizard"));
const ProcurementPage = React.lazy(() => import("./pages/ProcurementPage"));
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const RegisterPage = React.lazy(() => import("./pages/RegisterPage"));
const AuthCallbackPage = React.lazy(() => import("./pages/AuthCallbackPage"));
const ForgotPasswordPage = React.lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
const SupplierOnboarding = React.lazy(() => import("./pages/SupplierOnboarding"));
const BuyerDashboard = React.lazy(() => import("./pages/BuyerDashboard"));
const SupplierDashboard = React.lazy(() => import("./pages/SupplierDashboard"));
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));

// Minimal loading fallback for code-split routes
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B1120]">
      <Loader2 className="w-8 h-8 animate-spin text-[#F59E0B]" />
    </div>
  );
}

// Auth callback handler
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<PageLoader />}>
        <AuthCallbackPage />
      </Suspense>
    );
  }

  return (
    <>
      <PageTracker />

      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ===== PUBLIC ROUTES (eager) ===== */}
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<HomePage />} />
          <Route path="/how-it-works" element={<HomePage />} />
          <Route path="/trust" element={<HomePage />} />
          <Route path="/faq" element={<ContactPage />} />
          <Route path="/terms" element={<HomePage />} />
          <Route path="/privacy" element={<HomePage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* ===== PRODUCTS (eager) ===== */}
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/:productId" element={<ProductDetailPage />} />
          <Route path="/quote" element={<QuotePage />} />
          <Route path="/cart" element={<CartCheckoutPage />} />
          <Route path="/checkout" element={<CartCheckoutPage />} />

          {/* ===== SERVICES (lazy) ===== */}
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/personal-shopper" element={<PersonalShopperPage />} />
          <Route path="/services/pickup-delivery" element={<PickupDeliveryPage />} />
          <Route path="/services/company-registration" element={<CompanyRegistrationPage />} />
          <Route path="/services/logistics" element={<PickupDeliveryPage />} />

          {/* ===== PROCUREMENT (lazy) ===== */}
          <Route path="/procurement" element={<ProcurementPage />} />
          <Route path="/procurement/:categorySlug" element={<ProcurementPage />} />
          <Route path="/procurement/request" element={<ProcurementPage />} />

          {/* For Buyers / Suppliers */}
          <Route path="/for-suppliers" element={<ForSuppliersPage />} />
          <Route path="/for-buyers" element={<ForBuyersPage />} />

          {/* ===== AUTH (lazy) ===== */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/supplier-onboarding/:token" element={<SupplierOnboarding />} />

          {/* ===== DASHBOARDS (lazy — heaviest pages) ===== */}
          <Route path="/dashboard" element={<BuyerDashboard />} />
          <Route path="/dashboard/*" element={<BuyerDashboard />} />
          <Route path="/buyer/*" element={<BuyerDashboard />} />
          <Route path="/supplier" element={<SupplierDashboard />} />
          <Route path="/supplier/*" element={<SupplierDashboard />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/*" element={<AdminDashboard />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <LanguageProvider>
        <CurrencyProvider>
          <AuthProvider>
            <QuoteProvider>
              <AppRouter />
            </QuoteProvider>
          </AuthProvider>
        </CurrencyProvider>
      </LanguageProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
